import { useEffect, useRef, useState } from "react";
import { Play, Pause, Youtube, Instagram } from "lucide-react";
import { FadeUp, SectionHeader } from "./Reveal";
import { MEDIA_SAMPLES, VIDEO_SAMPLES, SOCIALS } from "../../lib/content";

const SpotifyIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.6 0 12 0zm5.5 17.3c-.2.4-.7.5-1 .3-2.9-1.8-6.5-2.2-10.8-1.2-.4.1-.8-.2-.9-.6-.1-.4.2-.8.6-.9 4.7-1.1 8.7-.6 11.9 1.4.3.2.4.7.2 1zm1.5-3.3c-.3.4-.8.6-1.3.3-3.3-2-8.3-2.6-12.2-1.4-.5.1-1-.1-1.2-.6-.1-.5.1-1 .6-1.2 4.4-1.3 9.9-.7 13.7 1.6.5.3.6.9.4 1.3zm.1-3.4C15.2 8.3 8.7 8.1 5 9.2c-.6.2-1.2-.2-1.4-.7-.2-.6.2-1.2.7-1.4 4.3-1.3 11.4-1 15.9 1.6.5.3.7 1 .4 1.5-.3.5-1 .7-1.5.4z" />
  </svg>
);

const BARS = Array.from({ length: 48 }, (_, i) => 0.25 + Math.abs(Math.sin(i * 1.7)) * 0.75);

// Unified media bus — only ONE audio OR video plays at a time across the whole Sound section.
const MEDIA_BUS = { current: null };
const claim = (el) => {
  if (MEDIA_BUS.current && MEDIA_BUS.current !== el) {
    try { MEDIA_BUS.current.pause(); } catch (e) {}
  }
  MEDIA_BUS.current = el;
};
const release = (el) => {
  if (MEDIA_BUS.current === el) MEDIA_BUS.current = null;
};

const VideoCard = ({ item, index, aspectClass = "aspect-video" }) => {
  // hasStarted stays true once the user hits play; overlay never comes back until video ends.
  // This prevents the overlay from re-appearing when the browser auto-pauses during scrubbing / exit-fullscreen.
  const [hasStarted, setHasStarted] = useState(false);
  const videoRef = useRef(null);
  const hasVideo = !!item.src;

  const play = () => {
    const el = videoRef.current;
    if (!el) return;
    claim(el);
    el.play().then(() => setHasStarted(true)).catch(() => {});
  };

  const onEnded = () => {
    setHasStarted(false);
    release(videoRef.current);
  };

  return (
    <FadeUp delay={index * 0.1}>
      <div
        data-testid={`media-video-${index}`}
        className={`group relative overflow-hidden border border-[rgba(28,22,16,0.18)] ${aspectClass} bg-black shadow-[6px_6px_0_rgba(28,22,16,0.1)]`}
      >
        {hasVideo ? (
          <>
            <video
              ref={videoRef}
              src={item.src}
              poster={item.poster}
              preload="metadata"
              playsInline
              controls={hasStarted}
              controlsList="nodownload"
              onEnded={onEnded}
              onPlay={() => { claim(videoRef.current); setHasStarted(true); }}
              className="w-full h-full object-contain"
            />
            {!hasStarted && (
              <button
                data-testid={`media-video-play-${index}`}
                onClick={play}
                aria-label={`Play ${item.title}`}
                className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4 text-center bg-gradient-to-t from-black/70 via-black/25 to-black/20 hover:from-black/80 hover:via-black/35 transition-colors duration-300"
              >
                <span className="w-14 h-14 flex items-center justify-center rounded-full border-2 border-[#DB0909] text-[#DB0909] bg-black/25 group-hover:bg-[#DB0909] group-hover:text-[#fdf6ec] transition-colors duration-300">
                  <Play size={20} className="ml-0.5" />
                </span>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white">{item.title}</p>
                <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/60">{item.subtitle}</p>
              </button>
            )}
          </>
        ) : (
          <>
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.05]"
              style={{ filter: "contrast(1.2) saturate(0.8) sepia(0.15) brightness(0.55)" }}
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4 text-center">
              <span className="w-14 h-14 flex items-center justify-center rounded-full border-2 border-[#DB0909] text-[#DB0909] group-hover:bg-[#DB0909] group-hover:text-[#fdf6ec] transition-colors duration-300">
                <Play size={20} className="ml-0.5" />
              </span>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white">{item.title}</p>
              <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/50">Coming soon</p>
            </div>
          </>
        )}
      </div>
    </FadeUp>
  );
};

// (uses shared MEDIA_BUS from top of file)

const fmtTime = (s) => {
  if (!isFinite(s)) return "";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

const AudioCard = ({ item, index }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [current, setCurrent] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const el = new Audio(item.src);
    el.preload = "metadata";
    el.volume = 0.9;
    audioRef.current = el;
    const onTime = () => {
      if (el.duration) {
        setProgress(el.currentTime / el.duration);
        setCurrent(el.currentTime);
      }
    };
    const onEnd = () => {
      setPlaying(false);
      setProgress(0);
      setCurrent(0);
      release(el);
    };
    const onPauseNative = () => setPlaying(false);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnd);
    el.addEventListener("pause", onPauseNative);
    return () => {
      el.pause();
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnd);
      el.removeEventListener("pause", onPauseNative);
      release(el);
    };
  }, [item.src]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      claim(el);
      el.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      el.pause();
      release(el);
    }
  };

  return (
    <FadeUp delay={index * 0.1}>
      <div
        data-testid={`media-audio-${index}`}
        className="border border-[rgba(28,22,16,0.14)] bg-[#fefefc] p-6 hover:border-[#950606]/50 transition-colors duration-500"
      >
        <div className="flex items-center gap-4">
          <button
            data-testid={`media-audio-play-${index}`}
            onClick={toggle}
            className="w-11 h-11 shrink-0 flex items-center justify-center border border-[#1c1610]/50 text-[#1c1610] hover:bg-[#DB0909] hover:border-[#DB0909] hover:text-[#f7f6f2] transition-colors duration-300"
          >
            {playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-[#1c1610] text-sm font-medium truncate">{item.title}</p>
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#4a3e30] mt-1">
              {item.meta}{playing && ` · ${fmtTime(current)}`}
            </p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-[3px] h-8 relative">
          {BARS.slice(0, 26).map((h, i) => {
            const filled = playing && progress * 26 >= i;
            return (
              <span
                key={i}
                className={playing ? `wavebar w-[3px] ${filled ? "bg-[#DB0909]" : "bg-[#950606]/40"}` : "w-[3px] bg-[#1c1610]/20"}
                style={{ height: `${h * 100}%`, animationDelay: `${i * 0.05}s`, animationPlayState: playing ? "running" : "paused" }}
              />
            );
          })}
        </div>
      </div>
    </FadeUp>
  );
};

export const Media = () => (
  <section id="media" data-testid="media-section" className="relative py-32 md:py-48">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12">
      <SectionHeader number="03" kicker="Portfolio">
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#1c1610]">
          <FadeUp>Hear the <em className="italic text-[#DB0909]">craft.</em></FadeUp>
        </h2>
      </SectionHeader>

      <div className="mt-20">
        <FadeUp>
          <div className="flex items-baseline gap-4 flex-wrap">
            <h3 className="font-display font-black text-2xl sm:text-3xl text-[#1c1610] leading-none">Watch</h3>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#4a3e30]">Videos · click to play</p>
          </div>
        </FadeUp>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="media-watch-grid">
          {VIDEO_SAMPLES.map((v, i) => {
            const isPortrait = v.portrait;
            const wrapCls = isPortrait ? "md:row-span-2" : "";
            const cardAspect = isPortrait ? "aspect-[3/4] md:aspect-auto md:h-full" : "aspect-video";
            return (
              <div key={v.title} className={wrapCls}>
                <VideoCard item={v} index={i} aspectClass={cardAspect} />
              </div>
            );
          })}
        </div>

        <FadeUp className="mt-16">
          <div className="flex items-baseline gap-4 flex-wrap">
            <h3 className="font-display font-black text-2xl sm:text-3xl text-[#1c1610] leading-none">Listen</h3>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#4a3e30]">Audio recordings · tap to play</p>
          </div>
        </FadeUp>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {MEDIA_SAMPLES.map((m, i) => (
            <AudioCard key={m.title} item={m} index={i} />
          ))}
        </div>
      </div>

      <FadeUp delay={0.1} className="mt-20">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#950606]">Follow the sound</p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <a
            data-testid="media-instagram-link"
            href={SOCIALS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-4 border border-[rgba(28,22,16,0.14)] bg-[#fefefc] px-6 py-5 hover:border-[#950606]/60 transition-colors duration-300"
          >
            <span className="text-[#950606] group-hover:text-[#DB0909] transition-colors duration-300"><Instagram size={20} /></span>
            <span>
              <span className="block text-[#1c1610] text-sm font-medium">Instagram</span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.15em] text-[#4a3e30] mt-0.5">@wltrbrown</span>
            </span>
          </a>
          <a
            data-testid="media-youtube-link"
            href={SOCIALS.youtube}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-4 border border-[rgba(28,22,16,0.14)] bg-[#fefefc] px-6 py-5 hover:border-[#950606]/60 transition-colors duration-300"
          >
            <span className="text-[#950606] group-hover:text-[#DB0909] transition-colors duration-300"><Youtube size={20} /></span>
            <span>
              <span className="block text-[#1c1610] text-sm font-medium">YouTube</span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.15em] text-[#4a3e30] mt-0.5">@sanjithiswalterbrown</span>
            </span>
          </a>
          <a
            data-testid="media-spotify-link"
            href={SOCIALS.spotify}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-4 border border-[rgba(28,22,16,0.14)] bg-[#fefefc] px-6 py-5 hover:border-[#950606]/60 transition-colors duration-300"
          >
            <span className="text-[#950606] group-hover:text-[#DB0909] transition-colors duration-300"><SpotifyIcon /></span>
            <span>
              <span className="block text-[#1c1610] text-sm font-medium">Spotify</span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.15em] text-[#4a3e30] mt-0.5">Listen to originals</span>
            </span>
          </a>
        </div>
      </FadeUp>
    </div>
  </section>
);
